import { useEffect, useCallback, useRef, useState } from 'react';
import api from '../api';
import SocketPath from '../Webservice/websocketService';

interface WebSocketMessage<T> {
    operation: 'insert' | 'update' | 'replace' | 'delete';
    document: T;
    collection?: string;
}

type UpdateCallback<T> = (operation: 'insert' | 'update' | 'replace' | 'delete', document: T, collection?: string) => void;

interface PollingConfig {
    url: string;
    payload?: any;
}

export function useGenericWebSocket<T>(groupName: string, onUpdate: UpdateCallback<T>, pollingConfig?: PollingConfig) {
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const MAX_RECONNECT_ATTEMPTS = 2; // Fast switch for Serverless detection
    const [mode, setMode] = useState<'ws' | 'polling'>('ws');

    const startPolling = useCallback(async () => {
        if (pollingRef.current) return;
        setMode('polling');
        // console.log(`🔌 [Hybrid Mode] Falling back to Polling for: ${groupName}`);

        const finalPollUrl = pollingConfig?.url || `/api/${groupName}/list`;
        const payload = pollingConfig?.payload || { page: 1, limit: 100 };

        pollingRef.current = setInterval(async () => {
            try {
                // Using custom api instance to ensure tokens are attached
                const res = await api.post(finalPollUrl, payload);
                
                if (res.data && res.data.response === 'S' && res.data.data) {
                    const data = res.data.data;
                    if (Array.isArray(data)) {
                        data.forEach(doc => onUpdate('replace', doc));
                    } else {
                        onUpdate('replace', data);
                    }
                } else if (res.data) {
                    onUpdate('replace', res.data as T);
                }
            } catch (err) {
                console.error(`❌ [Polling Error] ${groupName}:`, err);
            }
        }, 10000); // Poll every 10 seconds
    }, [groupName, onUpdate, pollingConfig]);

    const connect = useCallback(() => {
        if (mode === 'polling') return;

        try {
            const wsUrl = `${SocketPath}/${groupName}/`;
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                reconnectAttemptsRef.current = 0;
                setMode('ws');
                // console.log(`✅ [WebSocket] Connection active for: ${groupName}`);
            };

            ws.onmessage = (event) => {
                try {
                    const data: WebSocketMessage<T> = JSON.parse(event.data);
                    if (data.operation && data.document) {
                        onUpdate(data.operation, data.document, data.collection);
                    }
                } catch (e) {
                    console.error(`❌ [WebSocket] Parse error for ${groupName}:`, e);
                }
            };

            ws.onerror = () => {
                if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
                    startPolling();
                }
            };

            ws.onclose = (event) => {
                wsRef.current = null;
                // If not manually closed (code 1000), try to reconnect
                if (event.code !== 1000 && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
                    reconnectAttemptsRef.current++;
                    reconnectTimeoutRef.current = setTimeout(connect, 3000);
                } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
                    startPolling();
                }
            };

            wsRef.current = ws;
        } catch (error) {
            console.error(`❌ [WebSocket] Critical error for ${groupName}:`, error);
            startPolling();
        }
    }, [groupName, onUpdate, mode, startPolling]);

    useEffect(() => {
        connect();

        // Cleanup function
        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
            if (wsRef.current) {
                wsRef.current.close(1000, 'Component unmounting');
                wsRef.current = null;
            }
        };
    }, [connect]);

    return { ws: wsRef.current, mode };
}
